/** @jsx jsx */
import React, { useState, useEffect, useCallback } from 'react';
import { jsx } from '@emotion/core';
import {
  InputErrorText,
  LegalStatement,
  Link,
  SheerIDLogo
} from './styled';
import { FormHeader } from './basic/FormHeader';
import {
  Checkbox,
  Alert,
  AlertVariant,
  Spacer,
  SpacerAxis,
  magma
} from 'react-magma-dom';
import {
  ProfileEventTypes,
  useProfileUpComm
} from './screens/Profile/ProfileData';
import { MarketOptIn } from './screens/Profile/MarketOptIn';
import { TimezoneSelect } from './screens/Profile/TimezoneSelect';
import { RequiredFieldNotice } from './basic/RequiredFieldNotice';
import { LoginInput } from './data-inputs/validated/LoginInput';
import { BirthYearInput } from './data-inputs/validated/BirthYearInput';
import { FirstNameInput } from './data-inputs/validated/FirstNameInput';
import { LastNameInput } from './data-inputs/validated/LastNameInput';
import { UserDataKeys } from './screens/Profile/UserDataKeys';
import { useTrackLoadUnload, trackingClient } from '../ext/tracking';
import { EnterAwareButton } from './magma-wrap/EnterAwareButton';
import { Card } from './magma-wrap/Card';
import { Marker } from './meta/Marker';
import { formatInstitutionCityStateData, getInstitutionById } from '../api';
import { DEV_HELP_GLOBAL } from '../dev/dev-barrier/safe-ref';
import {
  SHEERID_DOCUPLOAD_STARTED,
  CENGAGE_VERIFICATION_START
} from './screens/Profile/VerificationResponses';
import { FacultyDataKeys } from './screens/Profile/FacultyDataKeys';
import { getLocalStorageJsonItem, setLocalStorageItem } from '../utility';
import { DataInput } from './screens/Profile/data-input/DataInput';
import { InstitutionPickerCard } from './screens/Profile/institution/InstitutionPickerCard';

export const TEST_ID = {
  MARKER: 'marker-faculty-create-profile',
  ERR_TEST_ID: 'error-alert',
  SUBMIT_TEST_ID: 'submit-button',
  MANUAL_VERIFICATION_CHECKBOX: 'checkbox-manual-verification',
  MANUAL_VERIFICATION_LINK: 'link-manual-verification'
};

export interface FacultyCreateProfileProps {
  username?: string;
  institutionId?: string;
  registerErrors?: any;
}

export const FacultyCreateProfile: React.FunctionComponent<FacultyCreateProfileProps> = (
  props: FacultyCreateProfileProps
) => {
  const { username, institutionId, registerErrors } = props;

  const upComm = useProfileUpComm();

  const [error, setError] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Keeping this here in case business changes their mind
  const [resetInstitution, setResetInstitution] = useState(false);

  const [_devOverride, _setDevOverride] = useState(
    undefined as undefined | any
  );
  const [autoVerify, setAutoVerify] = useState(false);
  const [institutionLabel, setInstitutionLabel] = useState('');

  const uxmodeInstitutionId = upComm?.ctx?.overrides?.values?.[UserDataKeys.instId];
  const autoVerifyInstructor = upComm?.ctx?.overrides?.values?.[UserDataKeys.autoVerifyUser];
  
  const trackCategory = trackingClient.event.facultyRegistrationDetails;
  useTrackLoadUnload(trackCategory);

  const loginIdRegisterError = registerErrors ? registerErrors[UserDataKeys.login] : null;

  const handleSubmit = () => {

    
    upComm.sendMsg({
      type: ProfileEventTypes.dataSaved,
      data: {
        _devOverride,
        onError: (err: Error) => {
          if (err) {
            setError(err.message);
          }
        }
      }
    });
  };

  // Sending default institution to skip manual verification checks on UserService
  const setInstitutionToProfile = useCallback((instId) => {
    upComm?.sendMsg({
      type: ProfileEventTypes.dataChanged,
      data: {
        key: UserDataKeys.instId,
        value: instId
      }
    });
  }, [upComm]);

  const updateAgreement = (event: any) => {
    const { target } = event;
    setAgreeTerms(target.checked);
  };

  const isSubmitDisabled = useCallback(() => {
    return !(agreeTerms && upComm?.ctx.isValid);
  }, [agreeTerms, upComm]);

  const findInstitutionRegion = useCallback((instId: string) => {
    getInstitutionById(instId).then((response) => {
      if (response && response.result && response.result.ok) {
        if (response.body) {
          setInstitutionLabel(response.body.name + formatInstitutionCityStateData(response.body));
          setAutoVerify(response.body.autoVerify && !autoVerifyInstructor);
        }
      }
    }).catch(err => {
      setError(err.message);
      setResetInstitution(true);
    });
  }, []);

  const setUserNameToProfile = useCallback(() => {
    if (username && upComm && !loginIdRegisterError) {
      upComm.sendMsg({
        type: ProfileEventTypes.dataInit,
        data: {
          key: UserDataKeys.login,
          value: username
        }
      });
    }
  }, [loginIdRegisterError, upComm, username]);

  useEffect(() => {
    if (uxmodeInstitutionId) {
      findInstitutionRegion(uxmodeInstitutionId);
      setInstitutionToProfile(uxmodeInstitutionId);
    }
    else if (institutionId) {
      findInstitutionRegion(institutionId);
      setInstitutionToProfile(institutionId);
    } else {
      const institutionDetail = getLocalStorageJsonItem('institution');
      if (institutionDetail) {
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.supervisorName,
            value: institutionDetail[`${FacultyDataKeys.supervisorName}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.supervisorEmail,
            value: institutionDetail[`${FacultyDataKeys.supervisorEmail}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.supervisorPhone,
            value: institutionDetail[`${FacultyDataKeys.supervisorPhone}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.entityName,
            value: institutionDetail[`${FacultyDataKeys.entityName}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.entityCity,
            value: institutionDetail[`${FacultyDataKeys.entityCity}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.entityState,
            value: institutionDetail[`${FacultyDataKeys.entityState}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.entityCountry,
            value: institutionDetail[`${FacultyDataKeys.entityCountry}`],
          }
        });
        upComm?.sendMsg({
          type: ProfileEventTypes.dataChanged,
          data: {
            key: FacultyDataKeys.entityRegion,
            value: institutionDetail[`${FacultyDataKeys.entityRegion}`],
          }
        });
      } else {
        setResetInstitution(true);
      }
    }
    setUserNameToProfile();
  }, [institutionId, uxmodeInstitutionId, findInstitutionRegion, setInstitutionToProfile, setUserNameToProfile, upComm]);

  /* istanbul ignore next */
  window?.[DEV_HELP_GLOBAL]?.useCommand?.({
    id: 'sheer-id.register.doc-upload',
    display: 'SheerID Register Doc Upload',
    action: () => {
      _setDevOverride({
        facultyRegister: async () => {
          return {
            results: [
              {
                verificationData: '5f510a0c05abf2187356a994',
                verificationStatus: SHEERID_DOCUPLOAD_STARTED
              }
            ]
          };
        }
      });
    }
  });

  /* istanbul ignore next */
  window?.[DEV_HELP_GLOBAL]?.useCommand?.({
    id: 'mamnual.register',
    display: 'Manual Register',
    action: () => {
      _setDevOverride({
        facultyRegister: async () => {
          return {
            results: [
              {
                verificationStatus: CENGAGE_VERIFICATION_START
              }
            ]
          };
        }
      });
    }
  });

  return (
    <React.Fragment>
      <Marker testId={TEST_ID.MARKER}/>

      <FormHeader>
        Create Account
      </FormHeader>
      <RequiredFieldNotice/>

      {error && (
        <Alert variant={AlertVariant.warning} testId={TEST_ID.ERR_TEST_ID}>
          <InputErrorText>{error}</InputErrorText>
        </Alert>
      )}

      <LoginInput
        upComm={upComm}
        defaultValue={username}
        readonly={!!username && !loginIdRegisterError}
        onErrorBlur={e =>
          trackCategory.validationError({
            tags: { error: e, name: UserDataKeys.login }
          })
        }
        autoFocus
        registerErrorMessage={loginIdRegisterError}
      />
      {(institutionId || uxmodeInstitutionId) && !resetInstitution &&
      <DataInput
        readonly={true}
        dataLabel={`Institution`}
        value={institutionLabel}
        testId={'institution-label'}
        {...props}
      />
      }
      <FirstNameInput
        upComm={upComm}
        onErrorBlur={e =>
          trackCategory.validationError({
            tags: { error: e, name: UserDataKeys.firstName }
          })
        }
      />
      <LastNameInput
        upComm={upComm}
        onErrorBlur={e =>
          trackCategory.validationError({
            tags: { error: e, name: UserDataKeys.lastName }
          })
        }
      />
      <BirthYearInput
        upComm={upComm}
        onErrorBlur={e =>
          trackCategory.validationError({
            tags: { error: e, name: UserDataKeys.birthYear }
          })
        }
      />
      {((!!autoVerify && !institutionLabel) || !!resetInstitution) && <InstitutionPickerCard upComm={upComm}/>}
      <TimezoneSelect
        required={true}
        upComm={upComm}
        onFocus={() => {
          trackCategory.timezoneSelectionStart();
        }}
      />

      <MarketOptIn
        upComm={upComm}
        skipDataInit={false}
      />

      <LegalStatement>
        <Checkbox
          labelText="I agree to the Terms of Use and Privacy Policy"
          labelStyle={{ textAlign: 'left' }}
          onChange={updateAgreement}
          testId="register-check-agree-terms"
          id="register-check-agree-terms"
          checked={agreeTerms}
        />
      </LegalStatement>
      {!autoVerify && (
        <EnterAwareButton
          style={{ minWidth: '230px' }}
          isFullWidth={true}
          value="Next"
          disabled={isSubmitDisabled()}
          onClick={() => {
            trackingClient.event.facultyRegistrationDetails.nextButtonClick();
            handleSubmit();
            setLocalStorageItem('institution', null);
          }}
          testId={TEST_ID.SUBMIT_TEST_ID}
          id="submit-button"
        />
      )}
      {!!autoVerify && (
        <Card>
          <p style={{ textAlign: 'left', fontSize: 'medium', marginTop: '0' }}>
            Verify your identity as an instructor with SheerID to access Cengage learning materials.
          </p>
          <EnterAwareButton
            style={{ minWidth: '230px' }}
            isFullWidth={true}
            value="Verify"
            disabled={isSubmitDisabled()}
            onClick={() => {
              trackingClient.event.facultyRegistrationDetails.nextButtonClick();
              handleSubmit();
            }}
            testId={TEST_ID.SUBMIT_TEST_ID}
            id="submit-button"
          />
          <SheerIDLogo src="/img/SheerID-Logo.png" alt="SheerID Logo"/>
        </Card>
      )}
      <LegalStatement>
        <Link
          href="https://www.cengage.com/legal"
          testId="terms-of-service"
          target="_blank"
          onClick={() => {
            trackingClient.event.facultyRegistrationDetails.termsOfUseClick();
          }}
        >
          Terms of Use
        </Link>
        <Spacer axis={SpacerAxis.horizontal} size={magma.spaceScale.spacing07}/>
        <Link
          href="https://www.cengage.com/privacy/"
          testId="privacy-policy"
          target="_blank"
          onClick={() => {
            trackingClient.event.facultyRegistrationDetails.privacyPolicyClick();
          }}
        >
          Privacy Policy
        </Link>
      </LegalStatement>
    </React.Fragment>
  );
};
